import { tallyTokens } from "../../theme.tsx";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Grid, InputLabel } from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import ExplainPrompt from "./ExplainPrompt.tsx";
import ThumbUpPrompt from "./ThumbUpPrompt.tsx";
import ThumbDownPrompt from "./ThumbDownPrompt.tsx";
import TextToSpeechButton from "./TextToSpeechButton.tsx";
import SaveToDashboardPrompt from "./SaveToDashboardPrompt.tsx";
import ErrorBoundary from "./ErrorBoundary.tsx";
import ProgressMessage from "./ProgressMessage.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import { useTypingEffect } from "../../hooks/useTypingEffect.ts";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";

interface PromptPanelProps {
  children?: React.ReactNode;
  content: string;
  customContent?: React.ReactNode;
  role: string;
  exchange_id?: string;
  feedback?: string | null;
  user_comment?: string | null;
  progress_update?: boolean | null;
  isStreaming?: boolean;
  /** Tally is still working on this answer. */
  working?: boolean;
}

const PromptPanel: React.FC<PromptPanelProps> = (props) => {
  const [displayedContent, setDisplayedContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoggedIn = useTypedSelector((state) => state.authState.isLoggedIn);
  const { anonymousSession } = useTypedSelector((state) => state.authState);
  const displayFeedbackButtons = !(
    anonymousSession &&
    anonymousSession.signUpState &&
    !anonymousSession.signUpState.isComplete
  );
  // Check if this is a custom action response (negative IDs represent custom actions).
  const isCustomActionResponse =
    props.exchange_id && props.exchange_id.startsWith("-");
  const isVoiceAssistantEnabled = useFeatureFlag("voice_assistant");

  useEffect(() => {
    // Clean up the timeout when the component is about to be unmounted
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { displayed, typing } = useTypingEffect(
    props.content,
    !!props.isStreaming,
    { htmlAware: true, minDelayMs: 40, randomRangeMs: 60 },
  );

  useEffect(() => {
    setIsStreaming(typing);
    setDisplayedContent(displayed);
  }, [typing, displayed]);

  // Memoize expensive style calculations
  const containerStyles = useMemo(
    () => ({
      whiteSpace: "normal" as const,
      backgroundColor:
        props.role === "user" ? tallyTokens.brandWash : "transparent",
      color: props.role === "user" ? tallyTokens.ink : "inherit",
      border:
        props.role === "user" ? `1px solid ${tallyTokens.line}` : undefined,
      // Asymmetric corner marks the message as the user's, the way a spoken
      // turn does in a thread.
      borderRadius: props.role === "user" ? "16px 16px 5px 16px" : "0",
      padding: props.role === "user" ? "10px 15px" : "0",
      // The markdown paragraph brings its default 16px margins; inside the
      // bubble they only pad it out, so the outer ones are dropped and the
      // bubble hugs the text.
      ...(props.role === "user" && {
        "& p:first-of-type": { marginTop: 0 },
        "& p:last-of-type": { marginBottom: 0 },
      }),
      maxWidth: props.role === "user" ? "76%" : "100%",
      display: "inline-block",
      marginLeft: props.role === "user" ? "auto" : "0%",
    }),
    [props.role],
  );

  // Memoize content to display
  const finalContentToDisplay = useMemo(() => {
    return props.isStreaming ? displayedContent : props.content;
  }, [props.isStreaming, displayedContent, props.content]);

  return (
    <Box
      marginBottom={"4%"}
      sx={{
        "&:hover .prompt-actions, & .prompt-actions:focus-within": {
          opacity: 1,
        },
      }}
    >
      {/* Avatar and body sit in a flex row with a fixed 13px gap, rather than a
          12-column grid whose first column is far wider than the mark. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: "13px",
        }}
      >
        {/* Assistant's Avatar */}
        {props.role !== "user" && <AvatarPanel working={props.working} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: props.role === "user" ? "row-reverse" : "row",
              justifyContent:
                props.role === "user" ? "flex-start" : "flex-start",
            }}
          >
            <Box sx={containerStyles}>
              <InputLabel
                sx={{
                  color: tallyTokens.ink,
                  whiteSpace: "normal",
                  lineHeight: 1.6,
                }}
              >
                <ErrorBoundary>
                  <ProgressMessage progressUpdate={!!props.progress_update}>
                    <CustomReactMarkDown
                      content={finalContentToDisplay}
                      role={props.role}
                    ></CustomReactMarkDown>
                    {!isStreaming && props.customContent && (
                      <Box sx={{ marginTop: "12px" }}>
                        {props.customContent}
                      </Box>
                    )}
                  </ProgressMessage>
                </ErrorBoundary>
              </InputLabel>
            </Box>
          </Box>
        </Box>
      </Box>
      {props.role !== "user" &&
        displayFeedbackButtons &&
        !isCustomActionResponse && (
          <Grid
            position={"relative"}
            className="prompt-actions"
            sx={{
              // Aligned with the answer body: avatar (27px) + gap (13px).
              marginLeft: "40px",
              marginBottom: "6%",
              // Revealed on hover, as in the approved design. Devices that
              // cannot hover (touch, at any width) keep them visible.
              opacity: 0,
              "@media (hover: none)": { opacity: 1 },
              transition: "opacity 160ms ease",
            }}
          >
            <Grid container direction="row" position={"absolute"}>
              {props.feedback !== "bad" && (
                <Grid>
                  <ThumbUpPrompt
                    exchange_id={props.exchange_id}
                    clicked={props.feedback === "good"}
                  />
                </Grid>
              )}
              {props.feedback !== "good" && (
                <Grid>
                  <ThumbDownPrompt
                    exchange_id={props.exchange_id}
                    clicked={props.feedback === "bad"}
                  />
                </Grid>
              )}
              <Grid>
                {isLoggedIn && (
                  <ExplainPrompt exchange_id={props.exchange_id} />
                )}
              </Grid>
              <Grid>
                {isLoggedIn && isVoiceAssistantEnabled && (
                  <TextToSpeechButton
                    text={displayedContent}
                    exchange_id={props.exchange_id}
                  />
                )}
              </Grid>
              <Grid>
                {isLoggedIn && (
                  <SaveToDashboardPrompt exchangeId={props.exchange_id} />
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
    </Box>
  );
};

// Custom comparison function for React.memo
const areEqual = (
  prevProps: PromptPanelProps,
  nextProps: PromptPanelProps,
): boolean => {
  // Check if all props are the same
  const keys = Object.keys(nextProps) as Array<keyof PromptPanelProps>;

  for (const key of keys) {
    if (prevProps[key] !== nextProps[key]) {
      return false; // Props are different, component should re-render
    }
  }

  return true; // Props are the same, prevent re-render
};

export default React.memo(PromptPanel, areEqual);
